<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Run Bonus Process
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-form ref="bonusProcessForm" @submit.prevent="submit">
                  <v-row class="px-2 py-2" dense>
                    <!-- Bonus Type -->
                    <v-col cols="12" md="3">
                      <v-autocomplete
                        v-model="bonus_type_id.value.value"
                        :error-messages="bonus_type_id.errorMessage.value"
                        variant="outlined"
                        label="Bonus Type"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :loading="loading"
                        :items="getBonusTypeList"
                        item-title="name"
                        item-value="id"
                        prepend-inner-icon="mdi-gift-outline"
                        clearable
                        
                      ></v-autocomplete>
                    </v-col>

                    <!-- Process Scope -->
                    <v-col cols="12" md="3">
                      <v-autocomplete
                        v-model="process_scope.value.value"
                        :error-messages="process_scope.errorMessage.value"
                        variant="outlined"
                        label="Process Scope"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :loading="loading"
                        :items="getProcessScope"
                        item-title="label"
                        item-value="value"
                        prepend-inner-icon="mdi-format-list-bulleted"
                      ></v-autocomplete>
                    </v-col>

                    <!-- Employee -->
                    <v-col v-if="employeeInput" cols="12" md="3">
                      <v-autocomplete
                        v-model="employee_id.value.value"
                        :error-messages="employee_id.errorMessage.value"
                        variant="outlined"
                        label="Employee"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :loading="loading"
                        :items="getHrmEmployeeList"
                        item-title="name"
                        item-value="id"
                        prepend-inner-icon="mdi-account"
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <!-- Department -->
                    <v-col v-if="departmentInput" cols="12" md="3">
                      <v-autocomplete
                        v-model="department_id.value.value"
                        :error-messages="department_id.errorMessage.value"
                        variant="outlined"
                        label="Department"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :loading="loading"
                        :items="getHrmDepartmentList"
                        item-title="name"
                        item-value="id"
                        prepend-inner-icon="mdi-office-building"
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <!-- Process Date -->
                    <v-col cols="12" md="3">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        v-model="process_date.value.value"
                        :error-messages="process_date.errorMessage.value"
                        variant="outlined"
                        label="Process Date"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-inner-icon="mdi-calendar"
                        prepend-icon=""
                      ></v-date-input>
                    </v-col>

                    <!-- Bonus Percent -->
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="bonus_percent.value.value"
                        :error-messages="bonus_percent.errorMessage.value"
                        type="number"
                        variant="outlined"
                        label="Bonus Percent (%)"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-inner-icon="mdi-percent"
                      ></v-text-field>
                    </v-col>

                    <!-- Year -->
                    <v-col cols="12" md="3">
                      <v-autocomplete
                        v-model="year.value.value"
                        :error-messages="year.errorMessage.value"
                        :items="getYearList"
                        item-title="label"
                        item-value="value"
                        variant="outlined"
                        label="Year"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-month"
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <!-- Month -->
                    <v-col cols="12" md="3">
                      <v-autocomplete
                        v-model="month.value.value"
                        :error-messages="month.errorMessage.value"
                        :items="getMonthList"
                        item-title="label"
                        item-value="value"
                        variant="outlined"
                        label="Month"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-inner-icon="mdi-calendar-month"
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="revenue_stamp.value.value"
                        :error-messages="revenue_stamp.errorMessage.value"
                        type="number"
                        variant="outlined"
                        label="Revenue Stamp"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-inner-icon="mdi-seal"
                      ></v-text-field>
                    </v-col>

                  </v-row>

                  <v-card-actions class="justify-end  pa-4">
                    <v-btn
                      color="grey"
                      variant="flat"
                      type="button"
                      size="default"
                      @click="submitForm(0)"
                      :loading="loadingAction === 'draft'"
                    >
                      Draft
                    </v-btn>
                    <v-btn
                      color="primary"
                      variant="flat"
                      type="button"
                      size="default"
                      @click="submitForm(1)"
                      :loading="loadingAction === 'post'"
                    >
                      POST
                    </v-btn>
                  </v-card-actions>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

export default {
  name: "BonusTypeSetup",
  setup() {
    const store = useStore();
    const loading = ref(false);
    const loadingAction = ref(null); // 'draft' | 'post' | null
    const employeeInput = ref(false);
    const departmentInput = ref(false);

    const bonus_type_id = useField("bonus_type_id");
    const process_scope = useField("process_scope");
    const department_id = useField("department_id");
    const employee_id = useField("employee_id");
    const process_date = useField("process_date");
    const bonus_percent = useField("bonus_percent");
    const year = useField("year");
    const month = useField("month");
    const revenue_stamp = useField("revenue_stamp");
    
    const currentStatus = ref("");

    const submitForm = (status) => {
        currentStatus.value = status;
        loadingAction.value = status === 0 ? 'draft' : 'post';
        submit();
    };

    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        // bonus_type_id: yup
        //   .number()
        //   .typeError("Bonus Type is required")
        //   .required("Bonus Type is required"),

        // process_scope: yup
        //   .string()
        //   .required("Process Scope is required"),

        // department_id: yup
        //   .number()
        //   .nullable()
        //   .when("process_scope", {
        //     is: "department",
        //     then: (schema) =>
        //       schema
        //         .typeError("Department is required")
        //         .required("Department is required"),
        //   }),

        // employee_id: yup
        //   .number()
        //   .nullable()
        //   .when("process_scope", {
        //     is: "employee",
        //     then: (schema) =>
        //       schema
        //         .typeError("Employee is required")
        //         .required("Employee is required"),
        //   }),

        // process_date: yup
        //   .string()
        //   .required("Process Date is required"),

        // bonus_percent: yup
        //   .number()
        //   .typeError("Bonus Percent must be a number")
        //   .required("Bonus Percent is required"),

        // revenue_stamp: yup
        //   .number()
        //   .typeError("Revenue Stamp must be a number")
        //   .required("Revenue Stamp is required"),

        // year: yup
        //   .number()
        //   .typeError("Year must be a number")
        //   .required("Year is required"),

        // month: yup
        //   .number()
        //   .typeError("Month is required")
        //   .required("Month is required"),
      }),
    });



    watch(
      () => process_scope.value.value,
      (v) => {
        employeeInput.value = v === "employee";
        departmentInput.value = v === "department";
      }
    );

    const submit = handleSubmit((values) => {
      loading.value = true;

      const payload = {
        bonus_type_id: bonus_type_id.value.value,
        process_scope: process_scope.value.value,
        department_id : department_id.value.value,
        employee_id: employee_id.value.value,
        process_date: formattedDate(process_date.value.value),
        bonus_percent: bonus_percent.value.value,
        revenue_stamp:revenue_stamp.value.value,
        year: year.value.value,
        month: month.value.value,
        status: currentStatus.value,
      };


      store
        .dispatch("saveUpdateBonusProcess", payload)
        .then((res) => {
          toast.success(res.data.message || "Saved successfully");
          resetForm();
          store.dispatch("getBonusTypeList");
          loading.value = false;
          return;
        })
        .catch((err) => {
          toast.error(err.response?.data?.message || "Something went wrong");
        })
        .finally(() => {
          loading.value = false;
          loadingAction.value = null;
        });
    });

    return {
      loading,
      loadingAction,
      bonus_type_id,
      process_scope,
      department_id,
      employee_id,
      employeeInput,
      departmentInput,
      process_date,
      bonus_percent,
      revenue_stamp,
      year,
      month,
      submit,
      submitForm,
    };
  },

  data() {
    return {
      panel: [0],
    };
  },

  computed: {
    getBonusTypeList() {
      return this.$store.getters.getBonusTypeList;
    },
    getHrmEmployeeList() {
      return this.$store.getters.getHrmEmployeeList;
    },
    getHrmDepartmentList() {
      return this.$store.getters.getHrmDepartmentList;
    },
    getProcessScope() {
      return [
        { label: "All", value: "all" },
        { label: "Department", value: "department" },
        { label: "Employee", value: "employee" },
      ];
    },
    getMonthList() {
      return [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
      ];
    },
    getYearList() {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear - 5; i <= currentYear + 5; i++) {
            years.push(i);
        }
        return years;
    },
  },

  created() {
    this.$store.dispatch("getBonusTypeList");
    this.$store.dispatch("getHrmEmployeeList");
    this.$store.dispatch("getHrmDepartmentList");
  },
};
</script>

<style scoped>
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0;
  right: 0; bottom: 0;
  background-color: #ccc;
  border-radius: 34px;
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: #4CAF50;
}

input:checked + .slider:before {
  transform: translateX(22px);
}

</style>
